import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStoredUser } from '../../hooks/useAuth';
import HRSidebar from './components/HRSidebar';
import HRMyManager from './components/HRMyManager';
import HRResponseUpload from './components/HRResponseUpload';
import HRMyPayments from './components/HRMyPayments';
import HRMyRequisites from './components/HRMyRequisites';
import HRSalaryCalculator from './components/HRSalaryCalculator';

type Section = 'manager' | 'uploads' | 'payments' | 'requisites' | 'calculator';

const SECTION_TITLES: Record<Section, string> = {
  manager:    'Мой менеджер',
  uploads:    'Загрузка откликов',
  payments:   'Мои выплаты',
  requisites: 'Мои реквизиты',
  calculator: 'Калькулятор ЗП',
};

export default function HRManagerPage() {
  const navigate = useNavigate();
  const user = getStoredUser();
  const [activeSection, setActiveSection] = useState<Section>(() => {
    const saved = localStorage.getItem('hr_portal_active_section');
    const valid: Section[] = ['manager', 'uploads', 'payments', 'requisites', 'calculator'];
    return valid.includes(saved as Section) ? (saved as Section) : 'manager';
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSectionChange = (section: string) => {
    setActiveSection(section as Section);
    localStorage.setItem('hr_portal_active_section', section);
  };

  useEffect(() => {
    if (!user) navigate('/', { replace: true });
  }, [user, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('portal_auth_user');
    navigate('/', { replace: true });
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'manager':    return <HRMyManager />;
      case 'uploads':    return <HRResponseUpload />;
      case 'payments':   return <HRMyPayments />;
      case 'requisites': return <HRMyRequisites />;
      case 'calculator': return <HRSalaryCalculator />;
      default:           return <HRMyManager />;
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0d0f18] flex"
    >
      {/* Desktop sidebar */}
      <div className="hidden lg:flex">
        <HRSidebar
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          userName={user?.name ?? ''}
          onLogout={handleLogout}
        />
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative z-50 flex">
            <HRSidebar
              activeSection={activeSection}
              onSectionChange={(s) => {
                handleSectionChange(s);
                setMobileOpen(false);
              }}
              userName={user?.name ?? ''}
              onLogout={handleLogout}
            />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="flex items-center gap-4 px-6 lg:px-8 py-5 border-b border-[#1e2130] sticky top-0 bg-[#0d0f18] z-20">
          <button
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl border border-[#2a2d3e] text-slate-400 hover:text-white transition-colors cursor-pointer"
            onClick={() => setMobileOpen(true)}
          >
            <i className="ri-menu-line" />
          </button>

          <nav className="flex items-center gap-2 text-sm">
            <button
              onClick={() => navigate('/vacancy')}
              className="text-slate-600 hover:text-slate-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              Кабинет
            </button>
            <span className="text-slate-700">
              <i className="ri-arrow-right-s-line" />
            </span>
            <span className="text-slate-300">{SECTION_TITLES[activeSection]}</span>
          </nav>
        </header>

        {/* Page content */}
        <main className="flex-1 px-6 lg:px-10 py-8 overflow-y-auto">
          {renderSection()}
        </main>
      </div>
    </div>
  );
}
